import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { NavigationClickEvent, NavigationOpenEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  /** @type {Array<() => void>} */
  #megaMenuCleanupFns = [];
  /** @type {boolean} */
  #suppressNextFocusActivate = false;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onNavLinkClick);
    this.#setupInteractiveMegaMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onNavLinkClick);
    this.#cleanupInteractiveMegaMenu();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.contains(document.activeElement)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    event.preventDefault();
    this.#closeActiveItem(activeItem, true);
    this.#suppressNextFocusActivate = true;
    activeItem.focus();
  };

  #onNavLinkClick = (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const maincategory = this.#menuLinkTitle(link.closest('.menu-list__list-item'));
    if (!maincategory) return;

    const level3Panel = link.closest('.mega-menu__level-3-panel');
    if (
      level3Panel &&
      (link.matches('.mega-menu__level-3-link') || link.matches('.mega-menu__view-all-link'))
    ) {
      this.#dispatchNavigationClick(
        maincategory,
        this.#navText(level3Panel.querySelector('.mega-menu-heading')),
        this.#navText(link.querySelector('.wrap-text'))
      );
      return;
    }

    if (link.closest('.mega-menu')) {
      this.#dispatchNavigationClick(maincategory, this.#navText(link.querySelector('.wrap-text')));
      return;
    }

    if (link.matches('.menu-list__link')) {
      this.#dispatchNavigationClick(maincategory);
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (event instanceof FocusEvent && this.#suppressNextFocusActivate) {
      this.#suppressNextFocusActivate = false;
      return;
    }
    this.#suppressNextFocusActivate = false;

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    if (item.getAttribute('aria-haspopup') === 'true') {
      this.#dispatchNavigationOpen(this.#menuLinkTitle(item));
    }

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      // Instantly close the previous submenu when switching to avoid cross-fade flicker.
      const previousLi = previouslyActiveItem.closest('.menu-list__list-item');
      if (previousLi instanceof HTMLElement) {
        previousLi.dataset.switching = '';
        requestAnimationFrame(() => delete previousLi.dataset.switching);
      }
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.#isWithinActiveRegion(activeItem, relatedTarget)) return;

    // Let hover state settle before closing on pointer exits (Firefox-safe).
    if (event instanceof PointerEvent && event.type === 'pointerleave') {
      requestAnimationFrame(() => {
        if (!activeItem || activeItem !== this.#state.activeItem) return;
        if (this.#isPointerInsideActiveRegion(activeItem)) return;
        if (this.#isFocusInsideSubmenu(activeItem)) return;
        this.#closeActiveItem(activeItem, true);
      });
      return;
    }

    this.#closeActiveItem(activeItem);
  }

  /**
   * Close active item immediately.
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #closeActiveItem = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);
    // Keep submenu open while pointer/focus is still inside it.
    if (!force) {
      if (submenu?.matches(':hover')) return;
      if (document.activeElement instanceof Node && submenu?.contains(document.activeElement)) return;
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * @param {HTMLElement} activeItem
   * @param {Node} node
   * @returns {boolean}
   */
  #isWithinActiveRegion(activeItem, node) {
    const activeListItem = activeItem.closest('.menu-list__list-item');
    return activeListItem?.contains(node) ?? false;
  }

  /**
   * @param {HTMLElement} activeItem
   * @returns {boolean}
   */
  #isPointerInsideActiveRegion(activeItem) {
    const activeListItem = activeItem.closest('.menu-list__list-item');
    const submenu = findSubmenu(activeItem);
    return Boolean(activeListItem?.matches(':hover') || submenu?.matches(':hover'));
  }

  /**
   * @param {HTMLElement} activeItem
   * @returns {boolean}
   */
  #isFocusInsideSubmenu(activeItem) {
    const submenu = findSubmenu(activeItem);
    return document.activeElement instanceof Node && Boolean(submenu?.contains(document.activeElement));
  }

  #setupInteractiveMegaMenu() {
    this.#cleanupInteractiveMegaMenu();

    const megaMenuLists = this.querySelectorAll('.mega-menu__list--interactive');
    megaMenuLists.forEach((megaMenuList) => {
      if (!(megaMenuList instanceof HTMLElement)) return;

      const panels = Array.from(megaMenuList.querySelectorAll('.mega-menu__level-3-panel')).filter(
        (panel) => panel instanceof HTMLElement
      );
      const links = Array.from(megaMenuList.querySelectorAll('.mega-menu__level-2-link')).filter(
        (link) => link instanceof HTMLElement
      );
      const defaultPanelIndex = megaMenuList.dataset.defaultPanelIndex || '';
      const panelsByIndex = new Map(panels.map((panel) => [panel.dataset.menuPanelIndex || '', panel]));
      const hasDefaultPanel = defaultPanelIndex !== '' && panelsByIndex.has(defaultPanelIndex);
      let activePanelIndex = '';
      /** @type {HTMLElement[]} */
      let activePanelFocusableElements = [];
      /** @type {HTMLElement | null} */
      let activeLink = null;
      const panelFocusableSelector = [
        '.mega-menu__level-3-links .mega-menu__link',
        '.mega-menu__media-panel-content a[href]',
        '.mega-menu__media-panel-content button:not([disabled])',
        '.mega-menu__media-panel-content [tabindex]:not([tabindex="-1"])',
      ].join(', ');

      /**
       * @param {Element} element
       * @returns {boolean}
       */
      const isPanelFocusableElement = (element) => (
        element instanceof HTMLElement
        && !element.closest('[hidden], [aria-hidden="true"], [inert]')
        && !element.matches(':disabled')
      );

      /**
       * Collect focusable elements inside the active level-3 panel
       * (navigation links + media panel CTA/content links).
       * @param {HTMLElement} panel
       * @returns {HTMLElement[]}
       */
      const getPanelFocusableElements = (panel) => Array.from(
        panel.querySelectorAll(panelFocusableSelector)
      ).filter(isPanelFocusableElement);

      /**
       * @param {string} panelIndex
       * @returns {HTMLElement[]}
       */
      const getFocusableElementsForPanelIndex = (panelIndex) => {
        const panel = panelsByIndex.get(panelIndex);
        if (!(panel instanceof HTMLElement)) return [];
        return getPanelFocusableElements(panel);
      };

      /**
       * @param {HTMLElement} panel
       * @param {boolean} isActive
       */
      const setPanelState = (panel, isActive) => {
        panel.classList.toggle('is-active', isActive);
        panel.toggleAttribute('hidden', !isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.inert = !isActive;
      };

      /**
       * @param {string | undefined} panelIndex
       * @param {HTMLElement | null} [nextActiveLink]
       */
      const setActivePanel = (panelIndex, nextActiveLink = null) => {
        const requestedIndex = panelIndex || '';
        let targetPanelIndex = '';
        if (requestedIndex !== '' && panelsByIndex.has(requestedIndex)) {
          targetPanelIndex = requestedIndex;
        } else if (hasDefaultPanel) {
          targetPanelIndex = defaultPanelIndex;
        }

        const nextPanel = targetPanelIndex !== '' ? panelsByIndex.get(targetPanelIndex) : null;

        if (!(nextPanel instanceof HTMLElement)) {
          const previousPanel = panelsByIndex.get(activePanelIndex);
          if (previousPanel instanceof HTMLElement) {
            setPanelState(previousPanel, false);
          }
          activePanelIndex = '';
          activePanelFocusableElements = [];
        } else if (activePanelIndex !== targetPanelIndex) {
          const previousPanel = panelsByIndex.get(activePanelIndex);
          if (previousPanel instanceof HTMLElement) {
            setPanelState(previousPanel, false);
          }
          setPanelState(nextPanel, true);
          activePanelIndex = targetPanelIndex;
          activePanelFocusableElements = getFocusableElementsForPanelIndex(targetPanelIndex);
        }

        if (activeLink !== nextActiveLink) {
          if (activeLink instanceof HTMLElement) {
            if (activeLink.getAttribute('aria-expanded') !== 'false') {
              activeLink.setAttribute('aria-expanded', 'false');
            }
            activeLink.classList.remove('is-active');
          }

          activeLink = nextActiveLink;

          if (activeLink instanceof HTMLElement) {
            if (activeLink.getAttribute('aria-expanded') !== 'true') {
              activeLink.setAttribute('aria-expanded', 'true');
            }
            activeLink.classList.add('is-active');
          }
        }
      };

      // Initialize with a known state and keep non-active panels out of tab order.
      panels.forEach((panel) => setPanelState(panel, false));
      if (hasDefaultPanel) {
        setActivePanel(defaultPanelIndex, null);
      }

      const handleListPointerLeave = () => setActivePanel(hasDefaultPanel ? defaultPanelIndex : '', null);
      const handleListFocusOut = (event) => {
        const relatedTarget = event.relatedTarget;
        if (relatedTarget instanceof Node) {
          if (megaMenuList.contains(relatedTarget)) return;
          setActivePanel(hasDefaultPanel ? defaultPanelIndex : '', null);
          return;
        }

        // Some browsers can emit focusout with null relatedTarget.
        // Check active element after focus settles before resetting.
        queueMicrotask(() => {
          const currentActiveElement = document.activeElement;
          if (currentActiveElement instanceof Node && megaMenuList.contains(currentActiveElement)) return;
          setActivePanel(hasDefaultPanel ? defaultPanelIndex : '', null);
        });
      };

      /**
       * @param {HTMLElement} target
       * @returns {HTMLElement[] | null}
       */
      const getActivePanelContextForTarget = (target) => {
        if (activePanelFocusableElements.length === 0) return null;
        if (!activePanelFocusableElements.includes(target)) return null;
        return activePanelFocusableElements;
      };

      /**
       * @param {HTMLElement} level2Link
       * @returns {boolean}
       */
      const moveFocusFromLevel2ToPanel = (level2Link) => {
        const targetPanelIndex = level2Link.dataset.targetPanelIndex || '';
        if (targetPanelIndex === '') return false;

        const targetPanel = panelsByIndex.get(targetPanelIndex);
        if (!(targetPanel instanceof HTMLElement)) return false;

        const panelFocusableElements = getFocusableElementsForPanelIndex(targetPanelIndex);
        const firstPanelFocusableElement = panelFocusableElements[0];
        if (!(firstPanelFocusableElement instanceof HTMLElement)) return false;

        setActivePanel(targetPanelIndex, level2Link);
        firstPanelFocusableElement.focus();
        return true;
      };

      /**
       * Keep keyboard flow intuitive:
       * - Tab from an active level-2 item with a level-3 panel goes to first level-3 link
       * - Shift+Tab from the first level-3 link returns to the active level-2 item
       * @param {KeyboardEvent} event
       */
      const handleListKeyDown = (event) => {
        if (event.key !== 'Tab') return;

        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const level2Link = target.closest('.mega-menu__level-2-link');
        if (level2Link instanceof HTMLElement && !event.shiftKey) {
          if (moveFocusFromLevel2ToPanel(level2Link)) {
            event.preventDefault();
          }
          return;
        }

        const panelFocusableElements = getActivePanelContextForTarget(target);
        if (!panelFocusableElements) return;

        if (!event.shiftKey) {
          if (!(activeLink instanceof HTMLElement)) return;
          const lastPanelFocusableElement = panelFocusableElements[panelFocusableElements.length - 1];
          if (lastPanelFocusableElement !== target) return;

          const activeLevel2Index = links.indexOf(activeLink);
          if (activeLevel2Index === -1) return;

          const nextLevel2Link = links[activeLevel2Index + 1];
          if (!(nextLevel2Link instanceof HTMLElement)) return;

          event.preventDefault();
          nextLevel2Link.focus();
          return;
        }

        if (!(activeLink instanceof HTMLElement)) return;
        const firstPanelFocusableElement = panelFocusableElements[0];
        if (firstPanelFocusableElement !== target) return;

        event.preventDefault();
        activeLink.focus();
      };

      megaMenuList.addEventListener('pointerleave', handleListPointerLeave);
      megaMenuList.addEventListener('focusout', handleListFocusOut);
      megaMenuList.addEventListener('keydown', handleListKeyDown);

      const linkListeners = [];
      links.forEach((link) => {
        const targetPanelIndex = link.dataset.targetPanelIndex || '';
        const handlePointerEnter = () => setActivePanel(targetPanelIndex, link);
        const handleFocus = () => setActivePanel(targetPanelIndex, link);

        link.addEventListener('pointerenter', handlePointerEnter);
        link.addEventListener('focus', handleFocus);
        linkListeners.push(() => {
          link.removeEventListener('pointerenter', handlePointerEnter);
          link.removeEventListener('focus', handleFocus);
        });
      });

      this.#megaMenuCleanupFns.push(() => {
        megaMenuList.removeEventListener('pointerleave', handleListPointerLeave);
        megaMenuList.removeEventListener('focusout', handleListFocusOut);
        megaMenuList.removeEventListener('keydown', handleListKeyDown);
        linkListeners.forEach((cleanup) => cleanup());
      });
    });
  }

  #cleanupInteractiveMegaMenu() {
    this.#megaMenuCleanupFns.forEach((cleanup) => cleanup());
    this.#megaMenuCleanupFns = [];
  }

  /** @param {Element | null | undefined} root */
  #menuLinkTitle(root) {
    return (root?.querySelector('.menu-list__link-title')?.textContent ?? '').trim();
  }

  /** @param {Element | null | undefined} el */
  #navText(el) {
    return (el?.textContent ?? '').trim();
  }

  /** @param {string} maincategory */
  #dispatchNavigationOpen(maincategory) {
    maincategory && document.dispatchEvent(new NavigationOpenEvent(maincategory));
  }

  /**
   * @param {string} maincategory
   * @param {string} [subcategory]
   * @param {string} [subsubcategory]
   */
  #dispatchNavigationClick(maincategory, subcategory, subsubcategory) {
    document.dispatchEvent(new NavigationClickEvent(maincategory, subcategory, subsubcategory));
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
